import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {Card} from 'primeng/card';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Tooltip} from 'primeng/tooltip';
import {TranslocoPipe} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {EvaluationSubCriteriaDirectus} from '@frontend/directus';
import {ToastService} from '@frontend/toast';
import {
  AttributeConsistencyStatus,
  AuditInfo,
  EvaluationCriteriaResponse,
  EvaluationSubCriteriaAttributeResponse,
  EvaluationSubCriteriaImportanceScore,
  EvaluationSubCriteriaView,
  TenderPhaseType,
} from '@frontend/shared-api';
import {SubCriteriaTabComponent} from '../../../../../../../../shared/components/sub-criteria-tab/sub-criteria-tab.component';
import {TabStatusDirective} from '../../../../../../../../shared/tabs/tab-status.directive';
import {logTabRender} from '../../../../../../../../shared/tabs/tab-render-log.util';
import {
  AttributeConsistencyChange,
  AttributeScoreSelection,
  AttributeScoreVM,
  SubCriteriaPanelVM,
} from '../../../../../../../../shared/components/sub-criteria-tab/sub-criteria-tab.types';
import {SubCriteriaAttributeEvaluationService} from '../../../../../../../../shared/components/subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.service';
import {AttributeConsistencyService} from '../../../../../../../../shared/services/attribute-consistency.service';
import {
  AttributesByEvaluationCriteriaMap,
  SubCriteriaAttributeItem,
} from '../../../../../../../../shared/components/subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';
import {ImportanceScoreProgressService} from '../../../../../../../../shared/services/importance-score-progress.service';
import {SubcriteriaAttributeSaveService} from '../../../../../../../../shared/services/subcriteria-attribute-save.service';
import {SubcriteriaBadgeService} from '../../../../../../../../shared/services/subcriteria-badge.service';
import {SavedSubCriteriaAttributes, SubCriteriaTabVM} from './subcriteria-tabs.types';
import {TabsUnsavedRevealDirective} from '../../../../../../../../shared/tabs/tabs-unsaved-reveal.directive';
import {ATTRIBUTE_CONSISTENCY_STATUS_COLORS} from '../../../../../../../../shared/constants/attribute-consistency.constants';
import {isSC14} from '../../../../../../../../shared/constants/evaluation-criteria.constants';

@Component({
  selector: 'app-subcriteria-tabs',
  imports: [
    Card,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tooltip,
    TranslocoPipe,
    TranslateDirectusPipe,
    SubCriteriaTabComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './subcriteria-tabs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubCriteriaTabsComponent {
  private readonly evaluationService = inject(SubCriteriaAttributeEvaluationService);
  private readonly saveService = inject(SubcriteriaAttributeSaveService);
  private readonly badgeService = inject(SubcriteriaBadgeService);
  private readonly progressService = inject(ImportanceScoreProgressService);
  private readonly consistencyService = inject(AttributeConsistencyService);
  private readonly toast = inject(ToastService);

  readonly projectId = input.required<number>();
  readonly evaluationCriteriaData = input<EvaluationCriteriaResponse[]>([]);
  readonly attributesByEvaluationCriteria = input<AttributesByEvaluationCriteriaMap>({});
  readonly directusSubCriteria = input<EvaluationSubCriteriaDirectus[]>([]);
  readonly contributorEmail = input<string>();
  readonly disabled = input(false);
  readonly showConsistency = input(false);

  readonly saveSuccess = output<SavedSubCriteriaAttributes>();
  readonly attributesChanged = output<SavedSubCriteriaAttributes>();
  readonly unsavedScoresChanged = output<boolean>();

  protected readonly phase = TenderPhaseType.RFI;
  protected readonly consistencyWarningColor =
    ATTRIBUTE_CONSISTENCY_STATUS_COLORS[AttributeConsistencyStatus.REVIEW_REQUIRED];
  protected readonly consistencyClarificationColor =
    ATTRIBUTE_CONSISTENCY_STATUS_COLORS[AttributeConsistencyStatus.CLARIFICATION_REQUIRED];

  protected readonly activeTabIndex = signal(0);
  protected readonly lastSaveAuditInfo = signal<AuditInfo | null>(null);
  private readonly savingSubCriteriaId = signal<number | undefined>(undefined);
  private readonly unsavedScores = linkedSignal({
    source: () => this.contributorEmail(),
    computation: () => new Map<string, EvaluationSubCriteriaImportanceScore | undefined>(),
  });

  private readonly subCriteriaTabs = computed<SubCriteriaTabVM[]>(() => {
    const attributesByCriteria = this.attributesByEvaluationCriteria();
    const directusSubCriteria = this.directusSubCriteria();

    return this.evaluationCriteriaData()
      .flatMap((criteria) =>
        criteria.subCriteria
          .filter((subCriteria) => subCriteria.included)
          .map((subCriteria) =>
            this.toSubCriteriaTab(
              subCriteria,
              attributesByCriteria[criteria.id]?.[subCriteria.id] ?? [],
              directusSubCriteria,
            ),
          ),
      )
      .sort((first, second) => first.scNumber - second.scNumber);
  });

  protected readonly subCriteriaPanels = computed<SubCriteriaPanelVM[]>(() => {
    const unsavedScores = this.unsavedScores();
    const savingSubCriteriaId = this.savingSubCriteriaId();

    return this.subCriteriaTabs().map((tab) => {
      const attributes = this.applyEdits(tab, unsavedScores);

      return {
        id: tab.id,
        evaluationCriteriaId: tab.evaluationCriteriaId,
        directusId: tab.directusId,
        translations: tab.translations,
        status: tab.status,
        badges: this.badgeService.createBadgeListItems(attributes),
        hasUnsavedScores: attributes.some((attribute) => unsavedScores.has(attribute.directusId)),
        isSaveAllowed: !this.isSC14AllNotRelevant(tab.scNumber, attributes),
        isSaving: savingSubCriteriaId === tab.id,
        unresolvedConsistencyCount: this.consistencyService.countUnresolved(attributes),
        clarificationConsistencyCount:
          this.consistencyService.countClarificationRequired(attributes),
      };
    });
  });

  protected readonly hasSubCriteria = computed<boolean>(() => this.subCriteriaPanels().length > 0);

  onTabSelected(value: string | number): void {
    this.activeTabIndex.set(typeof value === 'string' ? Number.parseInt(value, 10) : value);

    logTabRender('SC', value);
  }

  onConsistencyChanged(change: AttributeConsistencyChange): void {
    const tab = this.subCriteriaTabs().find((item) => item.id === change.subCriteriaId);

    if (tab === undefined) {
      return;
    }

    this.attributesChanged.emit({
      evaluationCriteriaId: tab.evaluationCriteriaId,
      subCriteriaId: tab.id,
      attributes: tab.attributes.map((attribute) =>
        attribute.directusId === change.attributeDirectusId
          ? {...attribute, consistency: change.consistency}
          : attribute,
      ),
    });
  }

  onScoreSelected(selection: AttributeScoreSelection): void {
    const unsavedScores = new Map(this.unsavedScores());
    unsavedScores.set(selection.directusId, selection.score);
    this.unsavedScores.set(unsavedScores);

    this.unsavedScoresChanged.emit(true);
  }

  async onSave(subCriteriaId: number): Promise<void> {
    const tab = this.subCriteriaTabs().find((item) => item.id === subCriteriaId);

    if (tab === undefined) {
      return;
    }

    const unsavedScores = this.unsavedScores();
    const editedAttributes = this.applySavedScores(tab, unsavedScores);
    const changedAttributes = editedAttributes.filter((attribute) =>
      unsavedScores.has(attribute.directusId),
    );

    if (changedAttributes.length === 0) {
      return;
    }

    this.savingSubCriteriaId.set(subCriteriaId);

    try {
      const response: EvaluationSubCriteriaAttributeResponse = await firstValueFrom(
        this.evaluationService.saveSubCriteriaAttributeEvaluations(
          this.projectId(),
          tab.evaluationCriteriaId,
          tab.id,
          this.saveService.buildSaveRequest(changedAttributes),
          TenderPhaseType.RFI,
        ),
      );

      this.lastSaveAuditInfo.set(response.auditInfo ?? null);
      this.discardEdits(changedAttributes);
      this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
      this.saveSuccess.emit({
        evaluationCriteriaId: tab.evaluationCriteriaId,
        subCriteriaId: tab.id,
        attributes: this.saveService.processSaveResponses(editedAttributes, response.attributes),
      });
    } finally {
      this.savingSubCriteriaId.set(undefined);
    }
  }

  private toSubCriteriaTab(
    subCriteria: EvaluationSubCriteriaView,
    attributes: SubCriteriaAttributeItem[],
    directusSubCriteria: EvaluationSubCriteriaDirectus[],
  ): SubCriteriaTabVM {
    return {
      id: subCriteria.id,
      directusId: subCriteria.directusId,
      evaluationCriteriaId: subCriteria.evaluationCriteriaId,
      scNumber: subCriteria.scNumber,
      translations:
        directusSubCriteria.find((item) => item.id === subCriteria.directusId)?.translations ?? [],
      attributes: attributes,
      status: this.progressService.calculateAttributeCompletionStatus(attributes),
    };
  }

  private applyEdits(
    tab: SubCriteriaTabVM,
    unsavedScores: Map<string, EvaluationSubCriteriaImportanceScore | undefined>,
  ): AttributeScoreVM[] {
    return tab.attributes.map((attribute) => ({
      id: attribute.id,
      directusId: attribute.directusId,
      scaNumber: attribute.scaNumber,
      translations: attribute.translations,
      score: this.effectiveScore(
        unsavedScores,
        attribute.directusId,
        attribute.selectedImportanceScore,
      ),
      consistency: attribute.consistency,
    }));
  }

  private isSC14AllNotRelevant(scNumber: number, attributes: AttributeScoreVM[]): boolean {
    return (
      isSC14(scNumber) &&
      attributes.every(
        (attribute) => attribute.score === EvaluationSubCriteriaImportanceScore.NOT_RELEVANT,
      )
    );
  }

  private effectiveScore(
    unsavedScores: Map<string, EvaluationSubCriteriaImportanceScore | undefined>,
    directusId: string,
    savedScore: EvaluationSubCriteriaImportanceScore | undefined,
  ): EvaluationSubCriteriaImportanceScore | undefined {
    return unsavedScores.has(directusId) ? unsavedScores.get(directusId) : savedScore;
  }

  private applySavedScores(
    tab: SubCriteriaTabVM,
    unsavedScores: Map<string, EvaluationSubCriteriaImportanceScore | undefined>,
  ): SubCriteriaAttributeItem[] {
    return tab.attributes.map((attribute) => ({
      ...attribute,
      selectedImportanceScore: this.effectiveScore(
        unsavedScores,
        attribute.directusId,
        attribute.selectedImportanceScore,
      ),
    }));
  }

  private discardEdits(savedAttributes: SubCriteriaAttributeItem[]): void {
    const unsavedScores = new Map(this.unsavedScores());
    savedAttributes.forEach((attribute) => unsavedScores.delete(attribute.directusId));
    this.unsavedScores.set(unsavedScores);

    this.unsavedScoresChanged.emit(unsavedScores.size > 0);
  }
}
